import AttachmentIcon from '@material-ui/icons/Attachment';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import GS from '../../grafsync/grafsync';
import ImportStepper from '../ImportStepper';
import { globalStyles as styles } from '../style/gstheme';
import { sanitiseText, makeCancelable } from '../utils/utils';
import Warning from '../Warning';
import EnterProjectDetails from './EnterProjectDetails';
import SelectFile from './SelectFile';
import Upload from './Upload';

const steps = ['Select File', 'Override Project Details', 'Upload'];

const stateReset = {
  filename: null,
  started: false,
  file: null,
  normalisation: null,
  title: "",
  description: "",
  message: null,
  uuid: null,
  uploaded: false,
  isErrored: false,
  transfer: null,
  status: null,
  errorMessage: null
};

class FullProjectImport extends React.Component {

  constructor(props) {
    super(props);
    this.state = Object.assign(stateReset);
    this.cancellable = null;
  }

  componentWillUnmount() {
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    if (this.cancellable) this.cancellable.cancel();
  }

  onSelectFile = (data) => {
    // The next line shouldn't be necessary
    if (data.length === 0 || data.length > 1) return;
    this.setState({ filename: data[0].path, file: data[0], title: "", description: "" });
  };

  onTitleChange = (event) => {
    const cleanText = sanitiseText(event.target.value);
    this.setState({
      title: cleanText,
      ...(cleanText === "") && { description: "" }
    });
  };

  onDescChange = (event) => {
    const cleanText = sanitiseText(event.target.value);
    this.setState({ description: cleanText });
  };

  uploadFile = async () => {
    const { file } = this.state;
    try {
      await file.slice(0, 1).arrayBuffer();
    }
    catch (e) {
      this.setState({
        isErrored: true,
        errorMessage: "Cannot access file. It may have changed. Please start again."
      });
      return;
    }
    this.cancellable = makeCancelable(
      GS.uploadProject(file, this.props.projectType, this.state.title.trim(), this.state.description.trim())
        .then(resp => {
          if (this.cancellable && !this.cancellable.isCancelled()) {
            this.setState({ uuid: resp.filename, uploaded: true });
            this.watchUpload();
          }
        })
        .catch((e) => {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({
            isErrored: true,
            errorMessage: e.message
          });
        })
    );
    return this.cancellable;
  };

  watchUpload = () => {
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    this.hcTimeout = setTimeout(async () => {
      try {
        let status = await GS.uploadStatus(this.state.uuid);
        if (this.cancellable && !this.cancellable.isCancelled()) {
          this.setState({
            ...(status.transfer !== undefined) && { transfer: status.transfer },
            ...(status.normalisation) && { normalisation: status.normalisation },
            ...(status.message) && { message: status.message },
            ...(status.status) && { status: status.status },
            ...(status.stepCount) && { stepCount: status.stepCount }
          });
          // Keep looping until ready
          if (status.transfer === undefined) this.watchUpload();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({
          transfer: false,
          isErrored: true,
          errorMessage: e.message
        });
      }
    }, 2000);
  };

  getStepContent = (step) => {
    if (step === 2 && !this.state.uploaded) this.uploadFile();

    switch (step) {
      case 0:
        return <SelectFile
          classes={this.props.classes}
          filename={this.state.filename}
          onSelectFile={this.onSelectFile} />;
      case 1:
        return <div><div style={{
          backgroundColor: '#f5f5f5',
          marginBottom: '10px',
          borderRadius: '4px',
          padding: '18px',
          height: '250px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <div style={{
            border: 'solid 1px #9e9e9e',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            padding: 20,
            height: '200px',
            paddingBottom: 20,
            display: 'flex'
          }}>
            <div style={{ width: 200, marginRight: 24 }}>
              <Typography paragraph variant="body2">You can override any project titles and descriptions in the imported file.</Typography>
              <Typography paragraph variant="body2">This is optional. Leave the fields blank to use the existing titles and descriptions.</Typography>
            </div>
            <div style={{ flex: 1 }}>
              <EnterProjectDetails
                classes={this.props.classes}
                title={this.state.title}
                mandatory={false}
                description={this.state.description}
                handleTitleChange={this.onTitleChange}
                handleDescChange={this.onDescChange} />
            </div>
          </div>
        </div>
          {this.state.filename &&
            <Typography data-test="attached-file" style={{
              backgroundColor: '#eeeeee',
              fontWeight: 500,
              borderRadius: '6px',
              color: '#13294B',
              padding: '10px'
            }} paragraph>
              <AttachmentIcon style={{ verticalAlign: 'bottom', marginRight: '10px' }} />
              {this.state.filename}
            </Typography>
          }
        </div>;
      case 2:
        return <Upload
          classes={this.props.classes}
          allowMinorIssues={true}
          message={this.state.message}
          transfer={this.state.transfer}
          status={Array.isArray(this.state.status) ? this.state.status : [this.state.status]}
          filename={this.state.filename}
          stepCount={this.state.stepCount}
          normalisation={this.state.normalisation} />;
      default:
        return 'Unknown step';
    }
  };

  canContinue = (step) => {
    if (step === 0 && this.state.filename === null) return false;
    if (step === 2 && this.state.transfer === null) return false;
    return true;
  };

  canGoBack = (step) => {
    if (step === 2 && this.state.transfer !== false) return false;
    return true;
  };

  handleBack = () => this.setState({
    transfer: null,
    uploaded: false
  });

  onComplete = async () => {
    this.setState(Object.assign(stateReset));
    // We need to force a reload because loaded resources may have been impacted
    await GS.unload();
    const link = this.props.baseLink;
    this.props.history.push(link);
  };

  onExitWarning = () => this.setState(Object.assign(stateReset));

  render() {

    const { isErrored, errorMessage, transfer } = this.state;
    const { classes, importUnavailable, instructions } = this.props;

    if (isErrored) return <Warning message={errorMessage} onExitWarning={this.onExitWarning} />;

    return (
      <div>
        {instructions &&
          <Typography variant="body2" paragraph>{instructions}</Typography>
        }
        <div className={classes.import} style={{ maxWidth: '743px' }}>
          <ImportStepper
            importUnavailable={importUnavailable}
            steps={steps}
            getStepContent={this.getStepContent}
            title="Import data"
            errored={transfer === false}
            handleBack={this.handleBack}
            canGoBack={this.canGoBack}
            canContinue={this.canContinue}
            onError={this.onExitWarning}
            onComplete={this.onComplete} />
        </div>
      </div>
    );
  }
}

FullProjectImport.defaultProps = {
  importUnavailable: null
};

FullProjectImport.propTypes = {
  history: PropTypes.object,
  classes: PropTypes.object,
  baseLink: PropTypes.string,
  importUnavailable: PropTypes.string,
  instructions: PropTypes.string,
  projectType: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(FullProjectImport));